import { FilterDropdownProps } from "antd/lib/table/interface";
import { useState } from "react";

import ColumnFilterDropdown from "./index";

export interface ColumnFilterValuesParams {
  search: string;
  take: number;
  otherFilters: Record<string, string[]>;
  searchText: string;
}

export interface ColumnFilterValuesResponse {
  values: Array<{ value: string; label: string }>;
  total: number;
}

export type FetchColumnFilterValues = (
  column: string,
  params: ColumnFilterValuesParams,
) => Promise<ColumnFilterValuesResponse>;

/**
 * Reusable Excel-style AutoFilter state manager for any antd <Table>. Pairs with
 * <ColumnFilterDropdown/>: pass it the entity's "get distinct column values" API
 * call, and spread `buildFilterProps(column)` onto each filterable <Column/>.
 *
 * `onFilterChange` is called whenever a filter is applied/cleared so the caller
 * can reset its own pagination state (pagination is table-specific, so it isn't
 * managed here).
 */
export function useColumnFilters(
  fetchValues: FetchColumnFilterValues,
  searchText: string = "",
  onFilterChange?: () => void,
) {
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );

  const getOtherColumnFilters = (column: string): Record<string, string[]> => {
    const otherFilters = { ...columnFilters };
    delete otherFilters[column];
    return otherFilters;
  };

  const applyFilter = (column: string, values: string[]) => {
    setColumnFilters((prev) => ({ ...prev, [column]: values }));
    onFilterChange?.();
  };

  const clearFilter = (column: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
    onFilterChange?.();
  };

  const buildFilterProps = (column: string) => ({
    filterDropdown: (props: FilterDropdownProps) => (
      <ColumnFilterDropdown
        column={column}
        fetchValues={(params) => fetchValues(column, params)}
        selectedValues={columnFilters[column] ?? []}
        otherFilters={getOtherColumnFilters(column)}
        searchText={searchText}
        onApply={applyFilter}
        onClear={clearFilter}
        confirm={props.confirm}
        close={props.close}
      />
    ),
    filteredValue: columnFilters[column] ?? null,
  });

  return { columnFilters, applyFilter, clearFilter, buildFilterProps };
}
